import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/shared/lib/utils";
import { useTranslation } from "@/shared/i18n";
// The app's field treatment: ring rather than border, rounded-lg, xs shadow.
// Exported so every control that presents itself as a field — a select trigger,
// a combobox trigger — renders from this one definition instead of copying it.
const inputVariants = cva(
  `flex
   w-full
   rounded-lg
   bg-white
   px-3
   py-1
   text-sm
   leading-[1.5]
   shadow-xs
   caret-primary
   ring-1
   ring-inset
   ring-input
   transition
   duration-100
   ease-linear
   file:border-0
   file:bg-white
   file:text-sm
   file:font-medium
   file:text-foreground
   placeholder:text-muted-foreground
   selection:bg-primary/10
   autofill:rounded-lg
   hover:ring-neutral-400
   focus-visible:outline-hidden
   focus-visible:ring-2
   focus-visible:ring-primary
   disabled:cursor-not-allowed
   disabled:opacity-50`,
  {
    variants: {
      inputSize: {
        sm: "h-8",
        md: "h-9",
        // For fields that size to their content — a textarea grows with what is
        // typed into it, so a fixed height would fight it.
        auto: "",
      },
    },
    defaultVariants: {
      inputSize: "md",
    },
  },
);

type InputProps = Omit<React.ComponentProps<"input">, "placeholder"> &
  VariantProps<typeof inputVariants> & {
    placeholder?: string;
  };

const Input = ({
  ref,
  className,
  type,
  placeholder,
  inputSize,
  ...props
}: InputProps) => {
  const { t } = useTranslation();

  return (
    <input
      type={type}
      className={cn(inputVariants({ inputSize, className }))}
      ref={ref}
      placeholder={placeholder ? t(placeholder) : undefined}
      {...props}
    />
  );
};
Input.displayName = "Input";

export { Input, inputVariants };
